import { motion } from 'motion/react';
import { Link, useNavigate } from '@tanstack/react-router';
import { formatRelativeTime } from '@/lib/utils';
import { useUpdateArticle, useDeleteArticle } from '@/queries/articles';
import EyeIcon from '@/assets/common/eye.svg?react';
import MessageIcon from '@/assets/common/message-circle.svg?react';
import ClockIcon from '@/assets/common/clock.svg?react';
import styles from './ArticleCard.module.scss';
import type { Article } from '@/types/article';

interface ArticleCardProps {
  article: Article;
  index?: number;
  showAuthor?: boolean;
}

export function ArticleCard({ article, index = 0, showAuthor = false }: ArticleCardProps) {
  const navigate = useNavigate();
  const updateArticle = useUpdateArticle();
  const deleteArticle = useDeleteArticle();

  const handleTogglePublic = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    updateArticle.mutate({
      id: article.id,
      data: { is_public: !article.is_public },
    });
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate({
      to: '/articles/$articleId/edit',
      params: { articleId: article.id },
    });
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (confirm('Are you sure you want to delete this article?')) {
      deleteArticle.mutate(article.id, {
        onSuccess: () => {
          navigate({ to: '/articles' });
        },
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.3,
        delay: index * 0.05,
        type: 'spring',
        stiffness: 100,
      }}
      style={{ height: '100%', minWidth: 0 }}
    >
      <Link
        to="/articles/$articleId"
        params={{ articleId: article.id }}
        className={styles.articleCard}
      >
        <div className={styles.articleCard__header}>
          <h3 className={styles.articleCard__title}>{article.title}</h3>
          <button
            onClick={handleTogglePublic}
            className={styles.articleCard__toggle}
            title={article.is_public ? 'Make private' : 'Make public'}
            aria-label={article.is_public ? 'Make private' : 'Make public'}
          >
            <motion.span
              className={`${styles.articleCard__toggleIcon} ${
                article.is_public ? styles['articleCard__toggleIcon--active'] : ''
              }`}
            >
              {article.is_public ? '🌐' : '🔒'}
            </motion.span>
          </button>
        </div>

        {article.excerpt && (
          <p className={styles.articleCard__excerpt}>{article.excerpt}</p>
        )}

        <div className={styles.articleCard__stats}>
          {article.topic && (
            <span className={styles.articleCard__badge}>
              {article.topic}
            </span>
          )}
          <span className={`${styles.articleCard__badge} ${styles['articleCard__badge--secondary']}`}>
            <ClockIcon className={styles.articleCard__badgeIcon} />
            {article.time_to_read_minutes} min read
          </span>
          <span className={`${styles.articleCard__badge} ${styles['articleCard__badge--secondary']}`}>
            <EyeIcon className={styles.articleCard__badgeIcon} />
            {article.view_count}
          </span>
          <span className={`${styles.articleCard__badge} ${styles['articleCard__badge--secondary']}`}>
            <MessageIcon className={styles.articleCard__badgeIcon} />
            {article.comment_count}
          </span>
        </div>

        {article.tags && article.tags.length > 0 && (
          <div className={styles.articleCard__tags}>
            {article.tags.slice(0, 3).map((tag) => (
              <span key={tag} className={styles.articleCard__tag}>
                {tag}
              </span>
            ))}
            {article.tags.length > 3 && (
              <span className={styles.articleCard__tag}>+{article.tags.length - 3}</span>
            )}
          </div>
        )}

        <div className={styles.articleCard__footer}>
          <div className={styles.articleCard__meta}>
            {showAuthor && article.author && (
              <span className={styles.articleCard__metaItem}>
                By {article.author.display_name || article.author.email}
              </span>
            )}
            <span className={styles.articleCard__metaItem}>
              {article.published_at
                ? `Published ${formatRelativeTime(article.published_at)}`
                : `Created ${formatRelativeTime(article.created_at)}`}
            </span>
          </div>

          {!showAuthor && (
            <div className={styles.articleCard__actions}>
              <button
                onClick={handleEdit}
                className={styles.articleCard__actionButton}
              >
                Edit
              </button>
              <button
                onClick={handleDelete}
                className={`${styles.articleCard__actionButton} ${styles['articleCard__actionButton--danger']}`}
              >
                Delete
              </button>
            </div>
          )}
        </div>
      </Link>
    </motion.div>
  );
}
